import { http } from "./http";

/**
 * Single in-progress call as returned by
 * `GET /api/customer-dashboard/live-calls`. Mirrors the columns of
 * `spCustomerDashboard_LiveCallsList` so a detail report can show
 * everything the legacy portal had without extra round-trips.
 */
export interface LiveCallDto {
  cdrId: number;
  callId: string;
  /** ISO-8601 UTC. */
  timeUtc: string;
  /** ISO-8601 local (server-side conversion to portal local). */
  time: string;
  dnis: string;
  description: string;
  /** Seconds since the call was accepted. */
  duration: number;
  state: string;
  info: string;
}

export interface LiveCallsResponse {
  callsInActive: number;
  callsOutActive: number;
  chatsActive: number;
  total: number;
  calls: LiveCallDto[];
}

export interface OutResultsResponse {
  intervalMinutes: number;
  busy: number;
  noAnswer: number;
  error: number;
  shortCall: number;
}

/**
 * Inbound / outbound / chat KPI block. The compare fields are only
 * populated for inbound (V05) and outbound (V03); chat returns 0
 * because the legacy SP doesn't compute period-over-period there.
 */
export interface DashboardInfoDto {
  callsToday: number;
  secondsToday: number;
  trendToday: number;
  todayCompare: number;
  callsYesterday: number;
  secondsYesterday: number;

  callsThisWeek: number;
  secondsThisWeek: number;
  trendThisWeek: number;
  thisWeekCompare: number;
  callsLastWeek: number;
  secondsLastWeek: number;

  callsThisMonth: number;
  secondsThisMonth: number;
  trendThisMonth: number;
  thisMonthCompare: number;
  callsLastMonth: number;
  secondsLastMonth: number;
}

export interface DashboardStatsResponse {
  inbound: DashboardInfoDto | null;
  outbound: DashboardInfoDto | null;
  chat: DashboardInfoDto | null;
  permissions: string[];
}

export interface TrafficDayDto {
  /** "yyyy-MM-dd" calendar date (UTC). */
  date: string;
  callsIn: number;
  secondsIn: number;
  callsOut: number;
  secondsOut: number;
}

export interface MonthlyTrafficResponse {
  year: number;
  month: number;
  days: TrafficDayDto[];
}

export const customerDashboardApi = {
  liveCalls: (limit?: number) =>
    http<LiveCallsResponse>(
      `/api/customer-dashboard/live-calls${
        limit && limit > 0 ? `?limit=${limit}` : ""
      }`,
    ),
  outResults: (intervalMinutes = 24 * 60) =>
    http<OutResultsResponse>(
      `/api/customer-dashboard/out-results?intervalMinutes=${intervalMinutes}`,
    ),
  stats: () => http<DashboardStatsResponse>("/api/customer-dashboard/stats"),
  monthlyTraffic: (year?: number, month?: number) => {
    const qs = new URLSearchParams();
    if (year && year > 0) qs.set("year", String(year));
    if (month && month >= 1 && month <= 12) qs.set("month", String(month));
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return http<MonthlyTrafficResponse>(
      `/api/customer-dashboard/monthly-traffic${suffix}`,
    );
  },
};
